import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import DashLayout from "@/components/DashLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Car, Bike, Zap } from "lucide-react";

export default function AddVehicle() {
  const [plate, setPlate] = useState("");
  const [nickname, setNickname] = useState("");
  const [vehicleType, setVehicleType] = useState("car");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/vehicles", {
        plate_number: plate,
        nickname: nickname || plate,
        vehicle_type: vehicleType,
      });
      toast.success(`Card ${data.card.card_code} activated`);
      nav(`/dashboard/vehicles/${data.vehicle.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <DashLayout title="Add a vehicle">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-gray-600 mb-8 hover:text-[#002FA7]" data-testid="back-dashboard">
        <ArrowLeft size={14} /> Back to control room
      </Link>

      <div className="grid lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-7 space-y-6" data-testid="add-vehicle-form">
          <div>
            <label className="label-eyebrow block mb-3">Vehicle type</label>
            <div className="grid grid-cols-3 gap-3">
              {[
                { v: "car", label: "Car", icon: <Car size={20} /> },
                { v: "bike", label: "Bike", icon: <Bike size={20} /> },
                { v: "scooter", label: "Scooter", icon: <Zap size={20} /> },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.v}
                  onClick={() => setVehicleType(opt.v)}
                  className={`p-4 border text-left flex flex-col items-start gap-2 ${vehicleType === opt.v ? "border-[#002FA7] bg-[#002FA7]/5" : "border-gray-200 bg-white"}`}
                  data-testid={`type-${opt.v}`}
                >
                  {opt.icon}
                  <span className="font-display font-bold">{opt.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="label-eyebrow block mb-2">Plate number</label>
            <input
              data-testid="plate-input"
              className="input-flat font-mono text-lg tracking-wider uppercase"
              value={plate}
              onChange={(e) => setPlate(e.target.value.toUpperCase())}
              placeholder="1234 BHR"
              required
            />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Nickname <span className="text-gray-400 normal-case">(optional)</span></label>
            <input
              data-testid="nickname-input"
              className="input-flat"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              placeholder="My Toyota"
            />
          </div>

          <button type="submit" disabled={busy || !plate} className="btn btn-primary" data-testid="create-vehicle-button">
            {busy ? "Activating…" : "Activate card"}
          </button>
        </form>

        <aside className="lg:col-span-5">
          <div className="card-flat p-6 bg-[#111827] text-white" data-testid="add-vehicle-info">
            <div className="label-eyebrow text-gray-400">What you get</div>
            <h3 className="font-display font-bold text-2xl mt-3 mb-4">A unique QR card</h3>
            <ul className="text-sm space-y-3 text-gray-300">
              <li className="border-t border-gray-700 pt-3"><span className="font-mono text-xs text-gray-500">01 — </span>Unique 5-digit card code (QAB-xxxxx)</li>
              <li className="border-t border-gray-700 pt-3"><span className="font-mono text-xs text-gray-500">02 — </span>HMAC-signed scan URL</li>
              <li className="border-t border-gray-700 pt-3"><span className="font-mono text-xs text-gray-500">03 — </span>Print and stick on your windscreen</li>
              <li className="border-t border-gray-700 pt-3"><span className="font-mono text-xs text-gray-500">04 — </span>Active immediately</li>
            </ul>
          </div>
        </aside>
      </div>
    </DashLayout>
  );
}
